import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  Injector,
  NgZone,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import {
  ConfigurationService,
  FirebaseAppService,
  LiveUpdateService,
  NotificationService,
  RouterService,
  ScreenTrackingService,
  SplashScreenService,
  ThemeService,
} from '@app/core';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { ServiceRequestsRepository } from '@app/store';
import {
  IonApp,
  IonBadge,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonRouterOutlet,
  IonSplitPane,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { addIcons } from 'ionicons';
import {
  calendarOutline,
  homeOutline,
  personOutline,
  searchOutline,
} from 'ionicons/icons';
import { filter, take } from 'rxjs/operators';
import { HeapService } from './core/services/heap/heap.service';
import { ChatService } from './core/services/chat/chat.service';

const DESKTOP_BREAKPOINT_PX = 1024;
const LOADER_FADE_DELAY_MS = 100;
const LOADER_FADE_DURATION_MS = 300;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    CommonModule,
    IonApp,
    IonBadge,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonRouterOutlet,
    IonSplitPane,
    IonTitle,
    IonToolbar,
    RouterLink,
    RouterLinkActive,
    TranslocoPipe,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  isDesktop = false;

  readonly upcomingTodayCount$ =
    this._serviceRequestsRepository.upcomingTodayCount$;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly routerService: RouterService,
    private readonly themeService: ThemeService,
    private readonly screenTrackingService: ScreenTrackingService,
    private readonly firebaseAppService: FirebaseAppService,
    private readonly liveUpdateService: LiveUpdateService,
    private readonly splashScreenService: SplashScreenService,
    private readonly injector: Injector,
    private readonly _chatService: ChatService,
    private readonly heapService: HeapService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly _serviceRequestsRepository: ServiceRequestsRepository,
  ) {
    this.initialize();
    addIcons({
      homeOutline,
      searchOutline,
      calendarOutline,
      personOutline,
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  ngOnInit(): void {
    this.checkScreenSize();
    this.watchFirstNavigation();
  }

  private watchFirstNavigation(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            const loader = document.getElementById('initial-loader');
            if (loader) {
              loader.style.transition = `opacity ${LOADER_FADE_DURATION_MS}ms ease-out`;
              loader.style.opacity = '0';
              setTimeout(() => {
                loader.classList.add('hidden');
              }, LOADER_FADE_DURATION_MS);
            }
          }, LOADER_FADE_DELAY_MS);
        });
        this.splashScreenService.hide();
      });
  }

  private initialize(): void {
    LoggingService.log('Initializing app component');

    try {
      this.firebaseAppService.initialize();
      this.liveUpdateService.initialize();
      this.routerService.initialize();
      this.themeService.initialize();
      this.screenTrackingService.initialize();

      this.injector.get(NotificationService).initialize();
      this.injector.get(ConfigurationService).initialize();

      this.splashScreenService.hide();
      this.heapService.initialize();
    } catch (error) {
      LoggingService.error('Error during initialization:', error);
    }
  }

  private checkScreenSize(): void {
    this.isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT_PX;
  }
}
